import React from 'react';
import { Box, IconButton, Popover, Tooltip } from '@mui/material';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import { SUPPORTED_REACTIONS, getMessageOwnReactionTypes } from '../communityChatReactions';
import { PARTNER_BRAND } from '../../partnerDashboardTheme';

const chipSx = (isOwn) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1,
  py: 0.25,
  borderRadius: '999px',
  bgcolor: isOwn ? 'primary.light' : 'background.paper',
  border: '1px solid',
  borderColor: isOwn ? 'primary.main' : 'divider',
  fontSize: '0.75rem',
  color: isOwn ? 'primary.main' : 'text.secondary',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    bgcolor: isOwn ? 'primary.light' : 'action.hover',
  },
});

const CommunityMessageReactions = ({
  message,
  currentUserReactionContext,
  readOnly = false,
  enableOpener = true,
  reactionPickerAnchor = null,
  onOpenPicker,
  onClosePicker,
  onSelectReaction,
  onOpenDetail,
  isReacting = false,
}) => {
  const chips = message?.reactionChips ?? [];
  const ownReactionTypes = new Set(getMessageOwnReactionTypes(message, currentUserReactionContext));
  const isPickerOpen = Boolean(reactionPickerAnchor);
  const showOpener = enableOpener && !readOnly;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
      {chips.map((chip) => (
        <Box
          key={chip.type}
          component="button"
          type="button"
          onClick={() => onOpenDetail?.(message)}
          sx={{
            ...chipSx(chip.isOwn),
            border: 'none',
            font: 'inherit',
          }}
          aria-label={`${chip.emoji} ${chip.count} reactions`}
        >
          <span>{chip.emoji}</span>
          <span>{chip.count}</span>
        </Box>
      ))}

      {showOpener ? (
        <>
          <Tooltip title="Add reaction">
            <span>
              <IconButton
                size="small"
                onClick={(event) => onOpenPicker?.(event, message)}
                disabled={isReacting}
                aria-label="Add reaction"
                sx={{
                  width: 28,
                  height: 28,
                  color: 'text.disabled',
                  '&:hover': { color: PARTNER_BRAND, bgcolor: 'primary.light' },
                }}
              >
                <AddReactionOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>

          <Popover
            open={isPickerOpen}
            anchorEl={reactionPickerAnchor}
            onClose={onClosePicker}
            disablePortal={false}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  display: 'flex',
                  gap: 0.5,
                  p: 1,
                  borderRadius: '999px',
                  bgcolor: 'background.paper',
                  opacity: 1,
                  filter: 'none',
                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
                },
              },
            }}
          >
            {SUPPORTED_REACTIONS.map((reaction) => {
              const isAlreadyReacted = ownReactionTypes.has(reaction.type);

              return (
                <Box
                  key={reaction.type}
                  component="button"
                  type="button"
                  disabled={isReacting || isAlreadyReacted}
                  onClick={() => onSelectReaction?.(message, reaction.type)}
                  aria-label={`React with ${reaction.type}`}
                  aria-pressed={isAlreadyReacted}
                  sx={{
                    border: 'none',
                    m: 0,
                    p: 0,
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.25rem',
                    lineHeight: 1,
                    cursor: isReacting || isAlreadyReacted ? 'default' : 'pointer',
                    bgcolor: isAlreadyReacted ? 'action.hover' : 'transparent',
                    opacity: isAlreadyReacted ? 0.35 : 1,
                    filter: isAlreadyReacted ? 'grayscale(1)' : 'none',
                    WebkitTextFillColor: 'initial',
                    '&:hover': {
                      bgcolor: isReacting || isAlreadyReacted ? 'transparent' : 'primary.light',
                    },
                    '&:disabled': {
                      opacity: isAlreadyReacted ? 0.35 : 0.55,
                    },
                  }}
                >
                  {reaction.emoji}
                </Box>
              );
            })}
          </Popover>
        </>
      ) : null}
    </Box>
  );
};

export default CommunityMessageReactions;
